import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import color from "../../../../../constants/color";
import { paymentTermsOptions } from "../../../../../constants/data";
import { getDueDateFromPaymentTerm } from "../../../../../utils/Utilities";
import style from "./style";

const EditBillPage = ({
  item,
  itemList,
  updateBillHandler,
  loading,
  supplierList,
  getOneBillHandler,
  oneBillData,
}) => {
  useEffect(() => {
    getOneBillHandler(item?._id);
  }, []);

  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    supplier: "",
    billDate: dayjs(),
    dueDate: dayjs().add(3, "day"),
    billNo: "",
    orderNo: "",
    paymentTerms: "",
    productInformation: [
      {
        productId: "",
        name: "",
        description: "",
        unitCost: 0,
        quantity: 0,
        amount: 0,
      },
    ],
    discountPercentage: 0,
    taxPercentage: 0,
    total: 0,
    discountValue: 0,
    taxValue: 0,
    grandTotal: 0,
  });

  // Initialize form with API data
  useEffect(() => {
    if (item) {
      setFormData({
        supplier: oneBillData?.supplier?._id,
        billDate: dayjs(oneBillData?.billDate),
        dueDate: dayjs(oneBillData?.dueDate),
        billNo: oneBillData?.billNo,
        orderNo: oneBillData?.orderNo,
        paymentTerms: oneBillData?.paymentTerms || "Immediate", // Default if not provided
        productInformation: oneBillData?.productInformation || [
          {
            productId: "",
            name: "",
            description: "",
            unitCost: 0,
            quantity: 0,
            amount: 0,
          },
        ],
        discountPercentage: oneBillData?.discountPercentage || 0,
        taxPercentage: oneBillData?.taxPercentage || 0,
        total: oneBillData?.total || 0,
        discountValue: oneBillData?.discountValue || 0,
        taxValue: oneBillData?.taxValue || 0,
        grandTotal: oneBillData?.grandTotal || 0,
      });
    }
  }, [oneBillData]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleProductChange = (index, field, value) => {
    const updatedProducts = [...formData.productInformation];

    // Update the specific field
    updatedProducts[index][field] = value;

    // Calculate amount if quantity or unitCost changes
    if (field === "quantity" || field === "unitCost") {
      const quantity =
        field === "quantity"
          ? Number(value)
          : Number(updatedProducts[index].quantity);
      const unitCost =
        field === "unitCost"
          ? Number(value)
          : Number(updatedProducts[index].unitCost);
      updatedProducts[index].amount = quantity * unitCost;
    }

    setFormData((prev) => ({
      ...prev,
      productInformation: updatedProducts,
    }));

    // Recalculate totals
    calculateTotals(updatedProducts);
  };

  const calculateTotals = (products) => {
    const total = products.reduce((sum, item) => sum + (item.amount || 0), 0);
    const discountValue = total * (formData.discountPercentage / 100);
    const taxableAmount = total - discountValue;
    const taxValue = taxableAmount * (formData.taxPercentage / 100);
    const grandTotal = taxableAmount + taxValue;

    setFormData((prev) => ({
      ...prev,
      total,
      discountValue,
      taxValue,
      grandTotal,
    }));
  };

  const handlePercentageChange = (field, value) => {
    const numValue = Number(value) || 0;
    setFormData((prev) => ({
      ...prev,
      [field]: numValue,
    }));

    // Recalculate with new percentages
    calculateTotals(formData.productInformation);
  };

  const handleAddProduct = () => {
    setFormData((prev) => ({
      ...prev,
      productInformation: [
        ...prev.productInformation,
        {
          productId: "",
          name: "",
          description: "",
          unitCost: 0,
          quantity: 0,
          amount: 0,
        },
      ],
    }));
  };

  const handleRemoveProduct = (index) => {
    const updatedProducts = [...formData.productInformation];
    updatedProducts.splice(index, 1);

    setFormData((prev) => ({
      ...prev,
      productInformation: updatedProducts,
    }));

    calculateTotals(updatedProducts);
  };

  const handleSupplierSelect = (supplier) => {
    setFormData((prev) => ({
      ...prev,
      supplier: supplier._id,
    }));
  };

  const isFormValid = () => {
    if (
      !formData.supplier ||
      !formData.billNo ||
      !formData.orderNo ||
      !formData.paymentTerms
    ) {
      return false;
    }
    for (let product of formData.productInformation) {
      if (!product.description || !product.unitCost || !product.quantity) {
        return false;
      }
    }

    return true;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      billDate: formData.billDate.toISOString(),
      dueDate: formData.dueDate.toISOString(),
    };
    updateBillHandler(payload); // Call the update function passed as prop
  };

  const selectedSupplier =
    supplierList?.find((s) => s._id === formData.supplier) || null;

  const handlePaymentTermSelection = (value) => {
    handleInputChange("paymentTerms", value);
    const dueDate = getDueDateFromPaymentTerm(value, formData.billDate);
    if (dueDate?.isValid()) {
      handleInputChange("dueDate", dueDate.endOf("day"));
    }
  };

  return (
    <>
      <Box px="15px" mt={2}>
        <Box sx={style.headerContainer}>
          <Typography sx={style.leaveHeading}>Edit Bill</Typography>
        </Box>
      </Box>
      <form onSubmit={handleSubmit}>
        <Grid container sx={style.mainContainer}>
          <Grid item xs={12} lg={11.8} sx={style.gridbox}>
            <Box sx={style.inputSection}>
              <Box sx={style.inputBox}>
                <Typography sx={style.inputText}>Supplier</Typography>
                <Autocomplete
                  id="autocomplete"
                  options={[
                    { _id: "add_new", name: "+ New Supplier" },
                    ...(supplierList || []),
                  ]}
                  loading={loading}
                  getOptionLabel={(option) => option?.name}
                  value={selectedSupplier}
                  onChange={(_, newValue) => {
                    if (newValue?._id === "add_new") {
                      navigate("/dashboard/addNewSupplier"); // redirect page
                      return;
                    }
                    if (newValue) handleSupplierSelect(newValue);
                  }}
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      style={
                        option._id === "add_new"
                          ? { color: color.secondary }
                          : {}
                      }
                    >
                      {option.name}
                    </li>
                  )}
                  renderInput={(params) => (
                    <TextField
                      placeholder="Supplier"
                      {...params}
                      sx={{ ...style.modalInput }}
                      variant="outlined"
                    />
                  )}
                />
              </Box>
              <Box sx={style.inputBox}>
                <Typography sx={style.inputText}>Bill Number</Typography>
                <TextField
                  fullWidth
                  placeholder={"Bill Number"}
                  value={formData.billNo}
                  onChange={(e) => handleInputChange("billNo", e.target.value)}
                  sx={style.modalInput}
                />
              </Box>
              <Box sx={style.inputBox}>
                <Typography sx={style.inputText}>Order Number</Typography>
                <TextField
                  fullWidth
                  placeholder={"Order Number"}
                  required
                  value={formData.orderNo}
                  onChange={(e) => handleInputChange("orderNo", e.target.value)}
                  sx={style.modalInput}
                />
              </Box>
            </Box>
            <Box sx={style.inputSection}>
              <Box sx={style.inputBox}>
                <Typography sx={style.inputText}>Bill Date</Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={formData.billDate}
                    onChange={(newValue) =>
                      handleInputChange("billDate", newValue)
                    }
                    sx={{
                      ...style.modalInput,
                      "& .MuiInputAdornment-root .MuiIconButton-root": {
                        paddingRight: "23px !important",
                      },
                    }}
                    slotProps={{
                      field: {
                        readOnly: true,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>
              <Box sx={style.inputBox}>
                <Typography sx={style.inputText}>Due Date</Typography>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    value={formData.dueDate}
                    onChange={(newValue) =>
                      handleInputChange("dueDate", newValue)
                    }
                    sx={{
                      ...style.modalInput,
                      "& .MuiInputAdornment-root .MuiIconButton-root": {
                        paddingRight: "23px !important",
                      },
                    }}
                    slotProps={{
                      field: {
                        readOnly: true,
                      },
                    }}
                  />
                </LocalizationProvider>
              </Box>
              <Box sx={style.inputBox}>
                <FormControl
                  fullWidth
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      height: "2.5em",
                      borderRadius: "10px",
                    },
                  }}
                >
                  <Typography sx={style.inputText}>
                    Payment Terms<span style={{ color: "red" }}>*</span>
                  </Typography>
                  <Select
                    value={formData.paymentTerms}
                    onChange={(e) => {
                      handlePaymentTermSelection(e.target.value);
                    }}
                    fullWidth
                    displayEmpty
                    renderValue={(selected) =>
                      selected ? (
                        selected
                      ) : (
                        <span style={{ color: "#aaa" }}>
                          Select Payment Terms
                        </span>
                      )
                    }
                  >
                    {paymentTermsOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Grid>
          <Box sx={style.itemSection}>
            <Box
              sx={{ backgroundColor: "#28AA4A", width: "2%", height: 2 }}
            ></Box>
            <Typography sx={style.gridText}>Product & Services</Typography>
          </Box>
          <Grid item xs={12} lg={11.8} sx={style.gridbox}>
            {formData.productInformation.map((product, index) => (
              <Box sx={style.leaveSection} key={index}>
                <Grid container sx={style.itemGrid}>
                  <Grid item xs={12} lg={1.8}>
                    <Autocomplete
                      id="autocomplete"
                      options={[
                        { _id: "add_new", title: "+ New Product" },
                        ...(itemList || []),
                      ]}
                      getOptionLabel={(option) => option?.title}
                      value={
                        itemList?.find(
                          (item) => item._id === product.productId
                        ) || null
                      }
                      onChange={(event, newValue) => {
                        if (newValue) {
                          if (newValue?._id === "add_new") {
                            navigate("/dashboard/addNewItem"); // redirect page
                            return;
                          }
                          if (!newValue) {
                            handleProductChange("", index, "name");
                            handleProductChange("", index, "invoiceQuantity");
                            return;
                          }
                          const updatedProducts = [
                            ...formData.productInformation,
                          ];
                          updatedProducts[index] = {
                            ...updatedProducts[index],
                            productId: newValue._id,
                            name: newValue.title,
                            description: newValue.description || "",
                            unitCost: newValue.unitCost || 0,
                            quantity: updatedProducts[index].quantity,
                            amount:
                              updatedProducts[index].quantity *
                              (newValue.unitCost || 0),
                          };

                          setFormData((prev) => ({
                            ...prev,
                            productInformation: updatedProducts,
                          }));

                          calculateTotals(updatedProducts);
                        } else {
                          const updatedProducts = [
                            ...formData.productInformation,
                          ];
                          updatedProducts[index] = {
                            ...updatedProducts[index],
                            productId: "",
                            name: "",
                            description: "",
                            unitCost: 0,
                            amount: 0,
                          };

                          setFormData((prev) => ({
                            ...prev,
                            productInformation: updatedProducts,
                          }));

                          calculateTotals(updatedProducts);
                        }
                      }}
                      renderOption={(props, option) => (
                        <li
                          {...props}
                          style={
                            option._id === "add_new"
                              ? { color: color.secondary }
                              : {}
                          }
                        >
                          {option.title}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          placeholder="Product"
                          {...params}
                          sx={style.modalInput}
                          variant="outlined"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loading ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} lg={3.1}>
                    <TextField
                      required
                      fullWidth
                      placeholder="Description"
                      value={product.description}
                      onChange={(e) =>
                        handleProductChange(
                          index,
                          "description",
                          e.target.value
                        )
                      }
                      sx={style.modalInput}
                    />
                  </Grid>
                  <Grid item xs={12} lg={1.8}>
                    <TextField
                      required
                      fullWidth
                      type="number"
                      placeholder="Unit Cost"
                      value={product.unitCost}
                      onChange={(e) =>
                        handleProductChange(index, "unitCost", e.target.value)
                      }
                      sx={style.modalInput}
                    />
                  </Grid>
                  <Grid item xs={12} lg={1.8}>
                    <TextField
                      required
                      fullWidth
                      type="number"
                      placeholder="QTY"
                      value={product.quantity}
                      sx={style.modalInput}
                      onChange={(e) =>
                        handleProductChange(index, "quantity", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={9} lg={1.8}>
                    <TextField
                      required
                      fullWidth
                      disabled
                      type="number"
                      placeholder="Amount"
                      value={product.amount}
                      sx={style.modalInput}
                    />
                  </Grid>
                  {index === 0 && (
                    <Grid item xs={1.5} lg={0.5}>
                      <Box sx={style.addIcon} onClick={handleAddProduct}>
                        +
                      </Box>
                    </Grid>
                  )}
                  {index !== 0 && (
                    <Grid item xs={1.5} lg={0.5}>
                      <Box
                        sx={style.deleteIcon}
                        onClick={() => handleRemoveProduct(index)}
                      >
                        <DeleteOutlineIcon />
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Box>
            ))}
          </Grid>
          <Grid container spacing={0}>
            <Grid item xs={12} md={7}>
              <Box sx={style.container4}>
                <Box
                  sx={{
                    height: "140px",
                    backgroundColor: "white",
                    borderRadius: "8px",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "#202020",
                      mb: 3,
                      display: "flex",
                      alignItems: "center",
                      "&::before": {
                        content: '""',
                        width: "3px",
                        height: "16px",
                        backgroundColor: "#28AA4A",
                        marginRight: "8px",
                        borderRadius: "2px",
                      },
                    }}
                  >
                    Discount and Tax (%)
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box>
                        <Typography
                          sx={{ fontSize: "14px", color: "#202020", mb: 0.5 }}
                        >
                          Discount
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          placeholder="Enter Discount"
                          value={formData.discountPercentage}
                          onChange={(e) =>
                            handlePercentageChange(
                              "discountPercentage",
                              e.target.value
                            )
                          }
                          inputProps={{ min: 0, max: 100 }}
                          sx={{
                            backgroundColor: "white",
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px !important",
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box>
                        <Typography
                          sx={{ fontSize: "14px", color: "#202020", mb: 0.5 }}
                        >
                          Tax
                        </Typography>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          placeholder="Enter Tax"
                          value={formData.taxPercentage}
                          onChange={(e) =>
                            handlePercentageChange(
                              "taxPercentage",
                              e.target.value
                            )
                          }
                          inputProps={{ min: 0, max: 100 }}
                          sx={{
                            backgroundColor: "white",
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px !important",
                            },
                          }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={5}>
              <Box sx={style.container3}>
                <Box
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "8px",
                    height: "140px",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography sx={{ color: "#666" }}>Total</Typography>
                    <Typography sx={{ fontWeight: 500 }}>
                      {formData.total.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography sx={{ color: "#666" }}>
                      Tax ({formData.taxPercentage}%)
                    </Typography>
                    <Typography sx={{ fontWeight: 500 }}>
                      {formData.taxValue.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography sx={{ color: "#666" }}>
                      Discount ({formData.discountPercentage}%)
                    </Typography>
                    <Typography sx={{ fontWeight: 500 }}>
                      {formData.discountValue.toFixed(2)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      pt: 1,
                      borderTop: "1px solid #eee",
                    }}
                  >
                    <Typography sx={{ fontWeight: 500 }}>
                      Grand Total
                    </Typography>
                    <Typography sx={{ fontWeight: 500, color: "#28AA4A" }}>
                      {formData.grandTotal.toFixed(2)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <Box sx={style.mainBoxes}>
          <Box sx={style.createJob}>
            <Button
              variant="contained"
              color="success"
              sx={style.cancelButtons}
            >
              Cancel
            </Button>
          </Box>
          <Box sx={style.createJob}>
            <Button
              variant="contained"
              type="submit"
              color="success"
              sx={style.jobButtons}
              disabled={!isFormValid()}
            >
              Save Changes
            </Button>
          </Box>
        </Box>
      </form>
    </>
  );
};
export default EditBillPage;
